import { Overlay } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
  output,
  SimpleChanges,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonToggleGroup, MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, of, switchMap, take, tap } from 'rxjs';

import { HttpMethod } from '@blaze-shared/api-contracts-domain';
import { SelectComponent } from '@blaze-shared/shared-feature';
import { ButtonComponent, ButtonIconComponent, TooltipDirective } from '@blaze-shared/shared-ui';
import { isNullish, mapStringsToOptions, SelectOption } from '@blaze-shared/shared-util';

import { ApiBuilderState, ApiBuilderStore } from '../+state/api-builder.store';
import {
  ApiActionForm,
  ApiActionStatus,
  type ApiBuilderAction,
  ApiBuilderActionData,
  ApiBuilderTestParameterDefinition,
  AssignParametersTestValuesDialogData,
  BodyType,
  ParametersExtendedFormArray,
  ParametersExtendedFormGroup,
} from '../api-builder.model';
import { AssignParametersTestValuesDialogComponent } from '../assign-parameters-test-values-dialog/assign-parameters-test-values-dialog.component';
import {
  BodyEditorDialogComponent,
  BodyEditorDialogData,
} from '../body-editor-dialog/body-editor-dialog.component';
import { ParametersListExtendedComponent } from '../parameters-list-extended/parameters-list-extended.component';
import {
  ResultPathSelectDialogComponent,
  ResultPathSelectDialogData,
} from '../result-path-select-dialog/result-path-select-dialog.component';
import {
  ResultSchemaDialogComponent,
  ResultSchemaDialogData,
} from '../result-schema-dialog/result-schema-dialog.component';
import { ApiBuilderService } from '../services/api-builder.service';
import { TestActionBtnComponent } from '../test-action-btn/test-action-btn.component';

@UntilDestroy()
@Component({
  selector: 'blaze-api-action-card',
  imports: [
    ButtonComponent,
    ButtonIconComponent,
    FormsModule,
    MatButtonToggleGroup,
    MatButtonToggleModule,
    MatExpansionModule,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    ParametersListExtendedComponent,
    ReactiveFormsModule,
    SelectComponent,
    TestActionBtnComponent,
    TooltipDirective,
  ],
  templateUrl: './api-action-card.component.html',
  styleUrls: ['./api-action-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApiActionCardComponent implements OnInit, OnChanges {
  private readonly apiBuilderService = inject(ApiBuilderService);
  private readonly apiBuilderStore = inject(ApiBuilderStore);
  private readonly dialog = inject(MatDialog);
  private readonly fb = inject(FormBuilder);
  private readonly injector = inject(Injector);
  private readonly overlay = inject(Overlay);

  actionChange = output<ApiBuilderAction>();

  @Input() accountId = '';
  @Input() action!: ApiBuilderAction;
  @Input() canDeleteAction = false;
  @Input() showValidation = false;

  readonly BodyType = BodyType;

  possibleMethods: SelectOption<HttpMethod>[];
  formCtrl!: FormGroup<ApiActionForm>;
  urlParametersCtrl!: ParametersExtendedFormArray;
  headersCtrl!: ParametersExtendedFormArray;
  preview!: ApiBuilderState;
  bodyTypeCtrl!: FormControl<BodyType>;
  isActionTested = false;
  isResponseSchemaCached = true;

  get showBodyCtrl(): boolean {
    const { httpMethod } = this.action.data;

    switch (httpMethod) {
      case HttpMethod.POST:
      case HttpMethod.PUT:
      case HttpMethod.PATCH:
        return true;
      default:
        return false;
    }
  }

  get showSetRootNodeBtn(): boolean {
    return this.action.status === ApiActionStatus.STAGING;
  }

  get bodyParametersLength(): number {
    return this.action.data.bodyParameters.length || 0;
  }

  get showResponseSchema(): boolean {
    return !!this.action.data.resultColumns?.length;
  }

  constructor() {
    /**
     * Keep select options in order.
     */
    this.possibleMethods =
      mapStringsToOptions([
        HttpMethod.GET,
        HttpMethod.POST,
        HttpMethod.PUT,
        HttpMethod.PATCH,
        HttpMethod.DELETE,
      ]) ?? [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['action']) {
      return;
    }

    const action = changes['action'].currentValue;
    if (this.isActionTested) {
      this.isResponseSchemaCached = false;
    }
    this.formCtrl?.patchValue(action, { emitEvent: false });
  }

  ngOnInit(): void {
    const { name, url, httpMethod, successStatusCode, useCookies, errorResultPath, bodyParameter } =
      this.action.data;

    this.urlParametersCtrl = new FormArray<ParametersExtendedFormGroup>([]);
    this.headersCtrl = new FormArray<ParametersExtendedFormGroup>([]);
    this.bodyTypeCtrl = new FormControl(bodyParameter ? BodyType.Dynamic : BodyType.Predefined, {
      nonNullable: true,
    });
    this.formCtrl = this.fb.group({
      name: new FormControl(name || '', { nonNullable: true, validators: Validators.required }),
      url: new FormControl(url || '', { nonNullable: true }),
      httpMethod: new FormControl(httpMethod || HttpMethod.GET, { nonNullable: true }),
      headers: this.headersCtrl,
      urlParameters: this.urlParametersCtrl,
      successStatusCode: new FormControl(successStatusCode || '200', {
        nonNullable: true,
        validators: [Validators.min(100), Validators.max(599)],
      }),
      useCookies: new FormControl(useCookies, { nonNullable: true }),
      errorResultPath: new FormControl(errorResultPath, { nonNullable: true }),
    });

    this.formCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe((data) => {
      this.actionChange.emit({
        ...this.action,
        data: {
          ...this.action.data,
          ...(data as ApiBuilderActionData),
          isTested: false,
        },

        // If user tries to edit values in action after it was confirmed, return to pristine state.
        status: ApiActionStatus.PRISTINE,
      });
    });

    this.bodyTypeCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe((bodyType) => {
      this.actionChange.emit({
        ...this.action,
        data: {
          ...this.action.data,
          bodyParameter: bodyType === BodyType.Predefined ? undefined : { name: 'body' },
        },

        // If user tries to edit values in action after it was confirmed, return to pristine state.
        status: ApiActionStatus.PRISTINE,
      });
    });
  }

  deleteAction(): void {
    this.apiBuilderStore.deleteAction(this.action);
  }

  testAction(): void {
    this.apiBuilderStore.common$
      .pipe(
        take(1),
        switchMap((common) => {
          const parameters = this.apiBuilderService.getAllParametersForTest(common, this.action);

          if (!parameters.length) {
            return of([]);
          }

          const data: AssignParametersTestValuesDialogData = { parameters };
          const dialogRef = this.dialog.open(AssignParametersTestValuesDialogComponent, {
            data,
            width: '375px',
            hasBackdrop: true,
            panelClass: ['bd', 'bd-dialog', 'bd-dialog-panel'],
          });

          return dialogRef.afterClosed();
        }),
        filter(
          (parameters): parameters is ApiBuilderTestParameterDefinition[] => !isNullish(parameters),
        ),
        tap((parameters) => {
          this.apiBuilderStore.setActionParametersTestValues({ action: this.action, parameters });
        }),
        switchMap(() => this.apiBuilderStore.getActionById$(this.action.id).pipe(take(1))),
      )
      .subscribe((action) => {
        if (action) {
          this.isActionTested = true;
          this.apiBuilderStore.testAction({ accountId: this.accountId, action });
        }
      });
  }

  confirmAction(): void {
    this.apiBuilderStore.confirmAction(this.action);
  }

  setRootNode(): void {
    const data: ResultPathSelectDialogData = {
      action: this.action,
    };

    this.dialog.open(ResultPathSelectDialogComponent, {
      injector: this.injector,
      hasBackdrop: true,
      panelClass: ['bd', 'bd-dialog', 'bd-dialog-full'],
      scrollStrategy: this.overlay.scrollStrategies.block(),
      data,
    });
  }

  editBody(): void {
    const data: BodyEditorDialogData = {
      action: this.action,
    };

    this.dialog.open(BodyEditorDialogComponent, {
      injector: this.injector,
      hasBackdrop: true,
      panelClass: ['bd', 'bd-dialog', 'bd-dialog-full'],
      scrollStrategy: this.overlay.scrollStrategies.block(),
      data,
    });
  }

  expandCard(): void {
    this.apiBuilderStore.setActionExpanded({
      action: this.action,
      expanded: !this.action.expanded,
    });
  }

  onShowResponseSchema(): void {
    const data: ResultSchemaDialogData = {
      action: this.action,
    };

    this.dialog.open(ResultSchemaDialogComponent, {
      injector: this.injector,
      hasBackdrop: true,
      panelClass: ['bd', 'bd-dialog', 'bd-dialog-full'],
      scrollStrategy: this.overlay.scrollStrategies.block(),
      data,
    });
  }
}
